const SPOTIFY_FORCE_REAUTH_STORAGE_KEY = "imags:spotify-force-reauth:v1";
const MAX_FORCE_REAUTH_AGE_MS = 60 * 60 * 1000;

export type SpotifyForceReauthState = {
  reason: string | null;
  message: string | null;
  createdAt: number;
};

type BuildSpotifyLoginPathOptions = {
  returnTo: string;
  native?: boolean;
  force?: boolean;
};

function getSessionStorage(): Storage | null {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function normalizeStoredState(raw: string | null): SpotifyForceReauthState | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<SpotifyForceReauthState>;
    const createdAt =
      typeof parsed.createdAt === "number" && Number.isFinite(parsed.createdAt)
        ? parsed.createdAt
        : 0;

    if (createdAt <= 0) return null;
    if (Date.now() - createdAt > MAX_FORCE_REAUTH_AGE_MS) return null;

    return {
      reason:
        typeof parsed.reason === "string" && parsed.reason.trim()
          ? parsed.reason.trim()
          : null,
      message:
        typeof parsed.message === "string" && parsed.message.trim()
          ? parsed.message.trim()
          : null,
      createdAt,
    };
  } catch {
    return null;
  }
}

export function markSpotifyForceReauth(reason: string, message?: string | null) {
  const storage = getSessionStorage();
  if (!storage) return;

  const payload: SpotifyForceReauthState = {
    reason: reason.trim() || null,
    message:
      typeof message === "string" && message.trim() ? message.trim() : null,
    createdAt: Date.now(),
  };

  try {
    storage.setItem(SPOTIFY_FORCE_REAUTH_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage failures. The fallback is the explicit force=1 link.
  }
}

export function clearSpotifyForceReauth() {
  const storage = getSessionStorage();
  if (!storage) return;

  try {
    storage.removeItem(SPOTIFY_FORCE_REAUTH_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

export function consumeSpotifyForceReauth(): SpotifyForceReauthState | null {
  const storage = getSessionStorage();
  if (!storage) return null;

  let normalized: SpotifyForceReauthState | null = null;
  try {
    normalized = normalizeStoredState(
      storage.getItem(SPOTIFY_FORCE_REAUTH_STORAGE_KEY)
    );
  } catch {
    normalized = null;
  }

  try {
    storage.removeItem(SPOTIFY_FORCE_REAUTH_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }

  return normalized;
}

export function buildSpotifyLoginPath({
  returnTo,
  native = false,
  force = false,
}: BuildSpotifyLoginPathOptions) {
  const params = new URLSearchParams({
    returnTo,
  });

  if (native) {
    params.set("native", "1");
  }

  if (force) {
    params.set("force", "1");
  }

  return `/api/spotify/login?${params.toString()}`;
}
